import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../lib/utils';
import DeckCardTile from './DeckCardTile';
import { RADIUS } from '../lib/medievalTheme';

/**
 * Shared fanned pack-reveal card. Renders the fan fly-in motion.div, the
 * rarity/foil glow aura, the foil-pivot reveal, the card box, the DeckCardTile
 * (always liveFoil so foils animate on reveal without needing hover) and the
 * hover name/rarity tooltip.
 *
 * Used by both the booster opener (ArenaPackOpening) and the draft/sealed pack
 * reveal (DraftPicker). The visual/behavioral differences between the two
 * callers are passed in as props so each keeps its exact look:
 *
 *  - entryOrigin{X,Y}: where the card flies in FROM, relative to its resting
 *    slot. The point the pack burst at. Omit for the plain rise-from-below.
 *  - entryInitialRotate: random fly-in rotation seed (caller supplies so the
 *    randomness lives at the call site, matching the originals).
 *  - animate{Opacity,Y,Scale}, fanRotate, zIndex: the resting fan transform.
 *  - entryDone: reveal-complete flag. Switches the spring between entry and
 *    settled, and (with entryFoil) gates the one-time foil pivot.
 *  - index: card index. Drives the staggered entry delay + foil-pivot delay.
 *  - entryFoil: whether this printing is foil (drives the foil pivot + aura).
 *  - auraClassName / glowStyle: the glow layer's className + inline style.
 *  - cardBoxStyle: the inner box border/filter/cursor style.
 *  - tileEntry: the DeckCardTile `entry` prop.
 *  - cornerBadge: element rendered in the tile corner (CardOwnedBadge).
 *  - onClick / onHoverChange: tile callbacks.
 *  - isHovered: drives the hover tooltip.
 *  - tooltip: { name, rarityLabel, rarityColorClass } for the hover tooltip.
 *  - faceDown / cardBackUrl: hold the card back-up until the player turns it
 *    over. The front is still mounted behind it, so the flip is one rotation
 *    rather than a swap, and nothing has to load at the moment of the reveal.
 *  - backRotated: the back art is landscape (the Atlas card) and needs turning
 *    upright inside the portrait slot.
 *  - justRevealed: fires the one-shot flash on the frame it turns over.
 *
 * Callers that pass none of these keep exactly their previous look, which is
 * how the draft/sealed reveal stays untouched.
 */
export default function PackRevealCard({
  faceDown = false,
  cardBackUrl,
  backRotated = false,
  justRevealed,
  entryInitialRotate,
  entryOriginX,
  entryOriginY,
  animateOpacity,
  animateY,
  animateScale,
  fanRotate,
  zIndex,
  entryDone,
  index,
  entryFoil,
  auraClassName,
  glowStyle,
  cardWidth,
  cardHeight,
  cardBoxStyle,
  tileEntry,
  cornerBadge,
  onClick,
  onHoverChange,
  isHovered,
  tooltip,
}) {
  // Foils get a one-time circular pivot once they've landed in the fan. The
  // keyframes trace a closed orbit around the rest pose, so the card visibly
  // tilts through every angle a player would normally catch by hand-wiggling.
  // the foil overlay's gradients sweep in response to the rotation, so the
  // prismatic shine actually animates instead of sitting still. Plays for
  // ~1.7s, then settles.
  // The pivot waits for the card to actually be face up: spinning a card back
  // to show off a foil sheen nobody can see yet is worse than not doing it.
  const foilPivotAnimate = entryFoil && entryDone && !faceDown ? {
    rotateX: [0, -10, -14, -10, 0, 10, 14, 10, 0],
    rotateY: [0, 10, 0, -10, -14, -10, 0, 10, 0],
  } : { rotateX: 0, rotateY: 0 };

  return (
    <motion.div
      className="relative"
      // With an origin supplied the card starts where the pack burst and
      // travels out to its place in the fan, so the reveal is the continuation
      // of the explosion rather than a second, unrelated animation. Without
      // one it keeps the old rise-from-below entry.
      initial={{
        opacity: 0,
        x: entryOriginX ?? 0,
        y: entryOriginY ?? '7.5cqw',
        scale: entryOriginX == null ? 0.3 : 0.16,
        rotate: entryInitialRotate,
      }}
      animate={{
        opacity: animateOpacity,
        x: 0,
        y: animateY,
        scale: animateScale,
        rotate: fanRotate,
        zIndex,
      }}
      transition={entryDone ? {
        type: 'spring',
        stiffness: 800,
        damping: 35,
        mass: 0.4,
        restDelta: 0.5,
        zIndex: { duration: 0 },
      } : {
        type: 'spring',
        stiffness: 250,
        damping: 20,
        delay: index * 0.06,
        zIndex: { duration: 0 },
      }}
    >
      {/* Rarity/foil glow. Rendered OUTSIDE the preserve-3d context so
          box-shadow works reliably on all platforms (Linux/CEF drops shadows
          inside 3D contexts). */}
      <div className={auraClassName} style={glowStyle} />

      {justRevealed ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: '-30%',
            borderRadius: '50%',
            background: 'radial-gradient(closest-side, rgba(255,252,240,0.95), rgba(255,226,150,0.35) 40%, rgba(255,226,150,0) 72%)',
            animation: 'vh-reveal-flash 620ms ease-out forwards',
            mixBlendMode: 'screen',
            pointerEvents: 'none',
            zIndex: 40,
          }}
        />
      ) : null}
      {/* The turn is its own transform, wrapping the pivot rather than sharing
          it. Folding both into one motion.div meant they shared a transition
          too. So the flip inherited the pivot's entry stagger and did not
          start until `0.25 + index * 0.04` seconds after the click, which for
          a card late in the fan is most of a second of nothing happening. */}
      <motion.div
        style={{ transformStyle: 'preserve-3d' }}
        animate={{ rotateY: faceDown ? 180 : 0 }}
        transition={{ type: 'spring', stiffness: 240, damping: 19, mass: 0.7 }}
      >
        <motion.div
          style={{ transformStyle: 'preserve-3d' }}
          animate={foilPivotAnimate}
          transition={entryFoil && entryDone ? {
            duration: 1.7,
            ease: 'easeInOut',
            delay: 0.25 + index * 0.04,
          } : { duration: 0 }}
        >
          <div
            style={{
              width: cardWidth,
              height: cardHeight,
              borderRadius: RADIUS.card,
              position: 'relative',
              ...cardBoxStyle,
              ...(cardBackUrl ? { backfaceVisibility: 'hidden' } : null),
            }}
          >
            <DeckCardTile
              entry={tileEntry}
              isSelected={false}
              liveFoil
              cornerBadge={cornerBadge}
              onClick={onClick}
              onHoverChange={onHoverChange}
            />
          </div>

        </motion.div>

        {cardBackUrl ? (
          <div
            onClick={onClick}
            onMouseEnter={() => onHoverChange?.(true)}
            onMouseLeave={() => onHoverChange?.(false)}
            style={{
              position: 'absolute',
              inset: 0,
              width: cardWidth,
              height: cardHeight,
              // Turned away from the viewer, so it is only visible while the
              // card is face down.
              transform: 'rotateY(180deg)',
              backfaceVisibility: 'hidden',
              cursor: 'pointer',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                // A site's back is the landscape Atlas card, so it is laid out
                // landscape and then turned upright. The slot stays portrait.
                width: backRotated ? cardHeight : cardWidth,
                height: backRotated ? cardWidth : cardHeight,
                // A positive CSS rotation is clockwise, but this sits inside a
                // face that is already mirrored by rotateY(180deg), and a
                // mirror reverses the sense of a rotation. So `90deg` here is
                // what the player sees as a quarter turn anticlockwise.
                transform: `translate(-50%, -50%) rotate(${backRotated ? 90 : 0}deg)`,
                backgroundImage: `url(${cardBackUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: backRotated ? RADIUS.cardLandscape : RADIUS.card,
                boxShadow: 'inset 0 0 0 1px rgba(255,220,140,0.35)',
              }}
            />
          </div>
        ) : null}
      </motion.div>
      <AnimatePresence>
        {isHovered && !faceDown ? (
          <motion.div
            className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap z-50 pointer-events-none"
            style={{ top: -36 }}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.12 }}
          >
            <div className="rounded-lg px-3 py-1.5 text-center" style={{ background: 'rgba(12,10,8,0.95)', border: '1px solid rgba(180,140,60,0.25)', backdropFilter: 'blur(12px)', boxShadow: '0 4px 16px rgba(0,0,0,0.5)' }}>
              <div className="text-xs font-semibold" style={{ color: '#e8d5a0' }}>{tooltip.name}</div>
              <div className={cn('text-[0.625cqw]', tooltip.rarityColorClass)}>{tooltip.rarityLabel}</div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
}
